import { useActivationDiscount } from "./useActivationDiscount";

import { useOrganisation } from "src/libraries/organisation/hooks";
import { useOptimizelyVariation } from "src/technical-integrations/optimizely/useOptimizelyVariation";
import {
  OptimizelyFlag,
  OptimizelyVariant,
} from "src/technical-integrations/optimizely/constants";

const MAX_ORGANISATION_AGE_ELIGIBLE_FOR_DISCOUNT_EXTENSION_CONTROL = 30;
const MAX_ORGANISATION_AGE_ELIGIBLE_FOR_DISCOUNT_EXTENSION_VARIANT = 14;

interface UseActivationDiscountCanExtend {
  canExtendDiscount: boolean;
  remainingDaysToExtend?: number;
}

export function useActivationDiscountCanExtend(): UseActivationDiscountCanExtend {
  const { variationKey: rewardsV2Variation } = useOptimizelyVariation({
    flag: OptimizelyFlag.TURBO_GROWTH_REWARDS_V2,
  });
  const isRewardsControl =
    !rewardsV2Variation || rewardsV2Variation === OptimizelyVariant.FLAG_OFF;

  const isRewardsVariation1 =
    rewardsV2Variation === OptimizelyVariant.VARIATION_1;
  const isRewardsVariation2 =
    rewardsV2Variation === OptimizelyVariant.VARIATION_2;
  const {
    firstPayoutDiscount,
    remainingDays = 0,
    completeSetup40DaysDiscount,
    firstCustomerOrPayment60DaysDiscount,
    firstCustomerOrPayment30DaysDiscount,
  } = useActivationDiscount();
  const organisation = useOrganisation();

  const organisationCreatedAtTimestamp = organisation?.created_at
    ? Math.floor(new Date(organisation?.created_at).getTime())
    : Date.now();
  const organisationCreationDays = Math.ceil(
    (Date.now() - organisationCreatedAtTimestamp) / 86400000
  ); // ms in one day

  const withinLimit = (limit: number) => organisationCreationDays <= limit;

  let canExtendDiscount = false;
  let remainingDaysToExtend = 0;

  if (isRewardsControl) {
    // CONTROL: up to 30 days old, and must NOT already have the first-payout discount.
    canExtendDiscount =
      withinLimit(
        MAX_ORGANISATION_AGE_ELIGIBLE_FOR_DISCOUNT_EXTENSION_CONTROL
      ) &&
      !firstPayoutDiscount &&
      remainingDays > 0;

    remainingDaysToExtend = Math.max(
      0,
      MAX_ORGANISATION_AGE_ELIGIBLE_FOR_DISCOUNT_EXTENSION_CONTROL -
        organisationCreationDays
    );
  } else if (isRewardsVariation1 && !firstCustomerOrPayment30DaysDiscount) {
    // VARIANT 1: up to 14 days old, and must NOT have the 30-day first-customer/payment discount.
    canExtendDiscount = withinLimit(
      MAX_ORGANISATION_AGE_ELIGIBLE_FOR_DISCOUNT_EXTENSION_VARIANT
    );

    remainingDaysToExtend = Math.max(
      0,
      MAX_ORGANISATION_AGE_ELIGIBLE_FOR_DISCOUNT_EXTENSION_VARIANT -
        organisationCreationDays
    );
  } else if (
    // VARIANT 2: up to 14 days old, and missing EITHER the 60-day or 40-day discount.
    isRewardsVariation2 &&
    (!firstCustomerOrPayment60DaysDiscount || !completeSetup40DaysDiscount)
  ) {
    canExtendDiscount = withinLimit(
      MAX_ORGANISATION_AGE_ELIGIBLE_FOR_DISCOUNT_EXTENSION_VARIANT
    );

    remainingDaysToExtend = Math.max(
      0,
      MAX_ORGANISATION_AGE_ELIGIBLE_FOR_DISCOUNT_EXTENSION_VARIANT -
        organisationCreationDays
    );
  }

  return { canExtendDiscount, remainingDaysToExtend };
}
